import {
  Autosuggest,
  Box,
  Button,
  ColumnLayout,
  DatePicker,
  ExpandableSection,
  Input,
  Modal,
  Multiselect,
  Pagination,
  Select,
  SelectProps,
  SpaceBetween,
  Table,
  Textarea,
} from '@cloudscape-design/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CollectRequest,
  Place,
  PlaceList,
  PlaceSearchItem,
  PlaceSearchItemWithName,
  SearchItem,
  SearchRequest,
} from '../common/types';
import { collectPlaces, searchPlaces } from '../common/api/places';
import { useMemo, useState } from 'react';
import {
  Container,
  Header,
  AppLayout,
  ContentLayout,
  Grid,
  Toggle,
  Form,
  Alert,
  FormField,
  StatusIndicator,
} from '@cloudscape-design/components';
import { ErrorMessage } from './ErrorMessage';
import { CreateListModal } from './CreateListModal';
import { getAllLists } from '../common/api/lists';
import { useCollection } from '@cloudscape-design/collection-hooks';

export type UpdatePlacesModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onComplete?: () => void;
  places: Place[];
};

export function UpdatePlacesModal(props: UpdatePlacesModalProps) {
  const { open, setOpen, onComplete, places } = props;

  const [names, setNames] = useState<string>('');

  const [crawl, setCrawl] = useState(true);
  const [reviews, setReviews] = useState(true);

  const close = () => {
    setCrawl(true);
    setReviews(true);
    setOpen(false);
    setNames('');
  };

  const collect = useMutation({
    mutationFn: collectPlaces,
    onSuccess: () => {
      if (onComplete) {
        onComplete();
      }
      collect.reset();
      close();
    },
  });

  const handleCollect = () => {
    if (collect.isPending) {
      return;
    }
    if (places.length === 0) {
      return;
    }
    const data: CollectRequest = {
      place_ids: places.map((place) => place.place_id),
      crawl,
      reviews,
      scorecards: true,
    };
    collect.mutate(data);
  };

  return (
    <Modal
      size="max"
      onDismiss={close}
      visible={open}
      closeAriaLabel="Close"
      header="Update Places"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close}>
              Cancel
            </Button>
            <Button
              loading={collect.isPending}
              variant="primary"
              onClick={handleCollect}
              disabled={places.length === 0}
            >
              Update Places
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <SpaceBetween size="m">
          <Table
            items={places}
            // pagination={<Pagination {...paginationProps} />}
            variant="embedded"
            columnDefinitions={[
              {
                id: 'name',
                header: 'Name',
                cell: (item) => <Box variant="p">{item.name}</Box>,
              },
              {
                id: 'address',
                header: 'Address',
                cell: (item) => <Box variant="p">{item.address}</Box>,
              },
            ]}
          />
        </SpaceBetween>
        <SpaceBetween size="m">
          <FormField label="Crawl">
            <Toggle
              checked={crawl}
              onChange={() => setCrawl(!crawl)}
              disabled={collect.isPending}
              description="Update the website crawl for each place"
            />
          </FormField>
          <FormField label="Reviews">
            <Toggle
              checked={reviews}
              onChange={() => setReviews(!reviews)}
              disabled={collect.isPending}
              description="Update the reviews for each place"
            />
          </FormField>
        </SpaceBetween>
        {collect.error && <ErrorMessage loading={collect.isPending} error={collect.error} />}
      </SpaceBetween>
    </Modal>
  );
}
